import Close from '@mui/icons-material/Close'
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slider,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'

/**
 * Full-screen modal for previewing prescription with uploaded image
 * Shows live preview as height is adjusted
 */
export default function PrescriptionPreviewModal({
  open,
  onClose,
  prescriptionTemplate,
  blobUrl,
  type, // 'header' or 'footer'
  defaultHeight,
  minHeight = 0.5,
  maxHeight = 5,
  onConfirm,
  isUploading,
  showSlider = false,
}) {
  const [adjustedHeight, setAdjustedHeight] = useState(defaultHeight || 2)
  const prescriptionViewSettingsData = useSelector(selectPrescriptionViewSettingsData)
  const prescriptionViewSettings = prescriptionViewSettingsData?.data

  // Create temporary template with blob URL and adjusted height
  const tempTemplate = {
    ...prescriptionTemplate,
    [type]: {
      ...(prescriptionTemplate?.[type] || {}),
      imageId: blobUrl, // Use blob URL for preview
      [`custom${type.charAt(0).toUpperCase() + type.slice(1)}Height`]: adjustedHeight,
    },
  }

  const handleConfirm = () => {
    onConfirm(adjustedHeight)
  }

  const handleSliderChange = (event, newValue) => {
    setAdjustedHeight(newValue)
  }

  const handleInputChange = (event) => {
    const value = event.target.value === '' ? '' : Number(event.target.value)
    if (value === '' || (value >= minHeight && value <= maxHeight)) {
      setAdjustedHeight(value === '' ? minHeight : value)
    }
  }

  return (
    <Dialog fullScreen open={open} onClose={isUploading ? undefined : onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={isUploading}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Adjust {type.charAt(0).toUpperCase() + type.slice(1)} Height
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ backgroundColor: '#f5f5f5', padding: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Height Adjustment Controls */}
          {showSlider && type !== 'footer' && (
            <Box
              sx={{
                backgroundColor: 'white',
                padding: 3,
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {type.charAt(0).toUpperCase() + type.slice(1)} Height (inches)
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={adjustedHeight}
                  onChange={handleSliderChange}
                  min={minHeight}
                  max={maxHeight}
                  step={0.1}
                  marks={[
                    { value: minHeight, label: `${minHeight}"` },
                    { value: maxHeight, label: `${maxHeight}"` },
                  ]}
                  valueLabelDisplay="on"
                  sx={{ flex: 1 }}
                  disabled={isUploading}
                />
                <TextField
                  value={adjustedHeight}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{
                    min: minHeight,
                    max: maxHeight,
                    step: 0.1,
                  }}
                  sx={{ width: 100 }}
                  size="small"
                  disabled={isUploading}
                />
              </Box>
            </Box>
          )}

          {/* Prescription Preview */}
          <Box
            sx={{
              backgroundColor: 'white',
              padding: 2,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <ViewOnlyPrescriptionV2
                prescriptionData={tempTemplate}
                hidePrintMarginsControl
                prescriptionViewSettings={prescriptionViewSettings}
                useFullUrlForHeaderImages={type === 'header'}
                // useFullUrlForHeaderImages
                useFullUrlForFooterImages={type === 'footer'}
                // useFullUrlForFooterImages
              />
            </Box>
            {/* <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <PMPParent>
                <PrescriptionHeader
                  prescriptionData={tempTemplate}
                  useCompleteImageLink={type === 'header'}
                  printType={PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterData}
                />
                <PmpBodyContentContainer>
                  <PmpPatientDetails
                    prescriptionData={{
                      ...tempTemplate,
                      patientPrefill: {
                        name: 'Patient Name',
                        vital: { dateOfBirth: '1983-12-19 00:00:00', gender: 'male' },
                      },
                    }}
                  />
                  <PmpContentContainer>
                    <div style={{ textAlign: 'center', padding: '200px 10px' }}>
                      ***** SAMPLE CONTENT *****
                    </div>
                  </PmpContentContainer>
                </PmpBodyContentContainer>
                <PrescriptionFooter
                  prescriptionData={tempTemplate}
                  useCompleteImageLink={type === 'footer'}
                  printType={PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterData}
                />
              </PMPParent>
            </Box> */}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 2, backgroundColor: 'white' }}>
        <Button onClick={onClose} disabled={isUploading} color="unFocused">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isUploading}
          variant="contained"
          color="primary"
          startIcon={isUploading && <CircularProgress size={20} />}
        >
          {isUploading ? 'Uploading...' : 'Confirm & Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
